import * as Yup from 'yup'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { genderList, constants, messages, regex } from '../Constants'
import { createUIDPatient } from '../../../services/ProrithmServices'
import {
  setUnidentifiedPatientDetails,
  selectUnidentifiedPatientDetails,
} from '../ProrithmSlice'
import {
  Box,
  Select,
  Button,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  FormHelperText,
} from '@mui/material'
import UnidentifiedPatient from '../UnidentifiedPatient'

interface MapUnidentifiedPatientFormValues {
  age: string
  gender: string
}

export const MapUnidentifiedPatientForm = () => {
  const dispatch = useAppDispatch()
  const unidentifiedPatientDetails = useAppSelector(
    selectUnidentifiedPatientDetails
  )

  const formik = useFormik<MapUnidentifiedPatientFormValues>({
    initialValues: {
      age: '',
      gender: '',
    },
    validationSchema: Yup.object({
      age: Yup.string()
        .matches(regex?.onlyNumerics, messages?.ageInvalid)
        .required(messages?.ageRequired),
      gender: Yup.string().required(messages?.genderRequired),
    }),
    onSubmit: () => {
      saveUnidentifiedPatient()
    },
  })

  const saveUnidentifiedPatient = async () => {
    const response = await createUIDPatient(
      formik?.values?.age,
      formik?.values?.gender
    )
    if (response) {
      toast(messages?.createUIDPatientSuccess, {
        position: 'bottom-center',
        hideProgressBar: true,
        closeButton: false,
        theme: 'dark',
        autoClose: 4000,
      })
      dispatch(setUnidentifiedPatientDetails(response))
      formik?.resetForm()
    } else {
      toast(messages?.createUIDPatientError, {
        position: 'bottom-center',
        hideProgressBar: true,
        closeButton: false,
        theme: 'dark',
        autoClose: 4000,
      })
    }
  }

  return (
    <>
      {!unidentifiedPatientDetails && (
        <Box
          display='flex'
          flexWrap='wrap'
          flexDirection='row'
          justifyContent='space-between'
        >
          <Box display='flex' flexWrap='wrap' flexDirection='row'>
            <Box minWidth={220} margin={1}>
              <TextField
                fullWidth
                name='age'
                label='Age'
                placeholder='Age'
                variant='outlined'
                value={formik?.values?.age}
                onBlur={formik?.handleBlur}
                onChange={formik?.handleChange}
                error={formik?.touched?.age && Boolean(formik?.errors?.age)}
                helperText={formik?.touched?.age && formik?.errors?.age}
              />
            </Box>
            <Box minWidth={220} margin={1}>
              <FormControl fullWidth>
                <InputLabel>{constants?.selectGender}</InputLabel>
                <Select
                  name='gender'
                  id='uid-patient-gender'
                  onBlur={formik?.handleBlur}
                  value={formik?.values?.gender}
                  onChange={formik?.handleChange}
                  label={constants?.selectGender}
                  error={
                    formik?.touched?.gender && Boolean(formik?.errors?.gender)
                  }
                >
                  {genderList?.map((item) => {
                    return (
                      <MenuItem key={item?.value} value={item?.value}>
                        {item?.label}
                      </MenuItem>
                    )
                  })}
                </Select>
                {formik?.touched?.gender && formik?.errors?.gender && (
                  <FormHelperText error>
                    {formik?.errors?.gender}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
          </Box>
          <Box display='flex' alignItems='center'>
            <Button
              variant='contained'
              onClick={() => formik?.handleSubmit()}
              disabled={!(formik?.isValid && formik?.dirty)}
            >
              {constants?.save}
            </Button>
          </Box>
        </Box>
      )}
      <UnidentifiedPatient />
    </>
  )
}
